// src/lib/pricing.ts
//
// Pricing utilities for the professional portal. Converts a catalog
// list price into a customer-specific net price using their tier.
//
// Per day-5 plan + M28.
//   - tier = "standard"   -> no discount (list price)
//   - tier = "preferred"  -> 8%  off list
//   - tier = "vip"        -> 15% off list
//   - tier = "quote"      -> contact-for-quote only; price is "—"
//
// Usage:
//   const result = calcNetPrice({ listPrice: 1000, tier: "vip", qty: 3 });
//   // -> { unitPrice: 850, lineTotal: 2550, discount: 0.15 }

import type { PricingTier } from "@/lib/demoData";
import { TIER_DISCOUNT } from "@/lib/demoData";

export interface NetPriceInput {
  /** Catalog list price (single unit). */
  listPrice: number;
  /** Customer's pricing tier. */
  tier: PricingTier;
  /** Quantity (defaults to 1). */
  qty?: number;
  /** Optional fractional rounding (defaults to 2 decimals). */
  decimals?: number;
}

export interface NetPriceResult {
  /** Per-unit net price after applying the tier discount. */
  unitPrice: number;
  /** unitPrice * qty. */
  lineTotal: number;
  /** The tier discount that was applied (0..1). Useful for labels. */
  discount: number;
  /** True if the customer must contact sales for a quote (no list price). */
  isQuoteOnly: boolean;
  /** Human-friendly label of the discount ("15%"). */
  discountLabel: string;
}

/**
 * Compute a tier-adjusted price for one line item.
 *
 * Negative or zero list prices are treated as "contact for quote".
 *
 * @example
 *   calcNetPrice({ listPrice: 1000, tier: "vip" })
 *   // -> { unitPrice: 850, lineTotal: 850, discount: 0.15, ... }
 */
export function calcNetPrice(input: NetPriceInput): NetPriceResult {
  const { listPrice, tier, qty = 1, decimals = 2 } = input;
  const discount = TIER_DISCOUNT[tier];

  if (discount === -1 || listPrice <= 0) {
    return {
      unitPrice: 0,
      lineTotal: 0,
      discount: 0,
      isQuoteOnly: true,
      discountLabel: formatDiscountLabel(0),
    };
  }

  const unitPrice = roundTo(listPrice * (1 - discount), decimals);
  const lineTotal = roundTo(unitPrice * Math.max(1, qty), decimals);

  return {
    unitPrice,
    lineTotal,
    discount,
    isQuoteOnly: false,
    discountLabel: formatDiscountLabel(discount),
  };
}

/**
 * Compute the totals across an array of line items (an order / cart).
 * Returns the per-line breakdown as well as the overall sum.
 *
 * @example
 *   summarizeOrder([
 *     { listPrice: 1000, tier: "vip", qty: 2 },
 *     { listPrice: 500, tier: "vip", qty: 1 },
 *   ]);
 *   // -> { lines: [...], subtotal: 2125, itemCount: 3 }
 */
export function summarizeOrder<
  T extends { listPrice: number; qty?: number; tier: PricingTier }
>(rows: T[]): OrderSummary<T> {
  const lines = rows.map((row) => ({
    row,
    price: calcNetPrice(row),
  }));
  const subtotal = roundTo(
    lines.reduce((sum, l) => sum + l.price.lineTotal, 0),
    2,
  );
  const itemCount = rows.reduce((sum, r) => sum + (r.qty ?? 1), 0);
  return { lines, subtotal, itemCount };
}

export interface OrderSummary<T> {
  lines: Array<{ row: T; price: NetPriceResult }>;
  subtotal: number;
  itemCount: number;
}

/** Format a discount as a percentage string: 0.15 -> "15%". */
export function formatDiscountLabel(discount: number): string {
  return `${Math.round(discount * 100)}%`;
}

/** Round to N decimal places without padding strings. */
function roundTo(n: number, decimals: number): number {
  const m = Math.pow(10, decimals);
  return Math.round(n * m) / m;
}
